import { useEffect, useMemo, useState, type ReactNode } from "react";
import { getPlatformHealth, type Application, type ManagementPermission } from "../api";
import type { Developer } from "../auth";
import { APP_VERSION } from "../config";
import { initials } from "../presentation";
import { isApplicationScoped, type ConsoleRoute, type ConsoleView } from "../routes";
import { Brand } from "./Brand";
import { Button, IconButton } from "./ui/Button";
import { Icon, type IconName } from "./ui/Icon";

interface ConsoleShellProps {
  readonly children: ReactNode;
  readonly developer: Developer | null;
  readonly route: ConsoleRoute;
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly navigationOpen: boolean;
  readonly onNavigate: (view: ConsoleView) => void;
  readonly onSelectApplication: (application: Application) => void;
  readonly onCloseNavigation: () => void;
  readonly onOpenNavigation: () => void;
  readonly onLogout: () => void;
  readonly permissions: readonly ManagementPermission[];
}

interface NavigationItem {
  readonly view: ConsoleView;
  readonly icon: IconName;
  readonly label: string;
  readonly description: string;
  readonly permission?: ManagementPermission;
}

interface NavigationSection {
  readonly id: string;
  readonly label: string;
  readonly items: readonly NavigationItem[];
}

const globalNavigation: readonly NavigationItem[] = [
  { view: "applications", icon: "apps", label: "应用中心", description: "创建和管理 IM 应用" },
];

const applicationNavigation: readonly NavigationSection[] = [
  { id: "application", label: "应用", items: [
    { view: "app-overview", icon: "activity", label: "应用概览", description: "接入进度与常用操作" },
  ] },
  { id: "integration", label: "接入与安全", items: [
    { view: "app-settings", icon: "settings", label: "基础配置", description: "平台类型与接入边界" },
    { view: "credentials", icon: "key", label: "凭证与安全", description: "AppKey、UserSig 与密钥" },
    { view: "quickstart", icon: "code", label: "SDK 快速接入", description: "多端 SDK 与示例代码" },
  ] },
  { id: "accounts", label: "用户与账号", items: [
    { view: "users", icon: "users", label: "IM 用户", description: "用户目录与创建", permission: "im.user.read" },
    { view: "system-accounts", icon: "server", label: "系统账号", description: "可信服务端发送身份", permission: "im.system_account.read" },
  ] },
  { id: "conversations", label: "群组与会话", items: [
    { view: "groups", icon: "groups", label: "群组管理", description: "群组、房间、频道与广播", permission: "im.group.read" },
  ] },
  { id: "messages", label: "消息管理", items: [
    { view: "message-send", icon: "server", label: "服务端发送", description: "使用系统账号发送消息", permission: "im.system_account.send" },
    { view: "server-messages", icon: "inbox", label: "消息记录", description: "可信服务端消息与链路", permission: "im.message.read" },
  ] },
  { id: "callbacks", label: "回调与事件", items: [
    { view: "callbacks", icon: "settings", label: "回调配置", description: "事件、签名与投递策略", permission: "im.callback.read" },
    { view: "callback-failures", icon: "warning", label: "投递日志", description: "失败、重试与死信诊断", permission: "im.callback_failure_log.read" },
  ] },
  { id: "governance", label: "数据治理", items: [
    { view: "retention", icon: "inbox", label: "消息保留策略", description: "按会话类型管理存档周期", permission: "im.retention.read" },
  ] },
];

/** 双作用域 SaaS 外壳：全局页面管理应用，应用页面始终携带明确 AppKey 上下文。 */
export function ConsoleShell({
  children,
  developer,
  route,
  application,
  applications,
  navigationOpen,
  onNavigate,
  onSelectApplication,
  onCloseNavigation,
  onOpenNavigation,
  onLogout,
  permissions,
}: ConsoleShellProps) {
  const currentSection = useMemo(
    () => applicationNavigation.find((section) => section.items.some((item) => item.view === route.view))?.id,
    [route.view],
  );
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(
    () => new Set(currentSection === undefined ? [] : [currentSection]),
  );
  const [health, setHealth] = useState<"checking" | "ok" | "unavailable">("checking");

  useEffect(() => {
    if (currentSection === undefined) return;
    setExpandedSections((current) => new Set([...current, currentSection]));
  }, [currentSection]);

  useEffect(() => {
    let active = true;
    const refresh = (): void => {
      void getPlatformHealth().then((result) => { if (active) setHealth(result); });
    };
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  function toggleSection(sectionId: string): void {
    setExpandedSections((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  const appScoped = isApplicationScoped(route.view);
  const contextTitle = appScoped && application !== null ? application.name : "开发者工作台";
  const contextSubtitle = appScoped && application !== null ? application.appKey : "全部应用";

  return <div className="console-shell">
    <button aria-label="关闭导航" className={`sidebar-scrim ${navigationOpen ? "visible" : ""}`} type="button" onClick={onCloseNavigation} />
    <aside className={`sidebar ${navigationOpen ? "sidebar-open" : ""}`}>
      <div className="sidebar-brand"><Brand inverse /></div>
      <label className="workspace-switcher" aria-label="切换应用工作区">
        <span className="workspace-icon">{application === null || !appScoped ? "ALL" : initials(application.name)}</span>
        <span><small>当前工作区</small><strong>{application === null || !appScoped ? "全部应用" : application.name}</strong></span>
        <select value={appScoped ? application?.appId ?? "" : ""} onChange={(event) => {
          const next = applications.find((candidate) => candidate.appId === event.currentTarget.value);
          if (next === undefined) onNavigate("applications");
          else onSelectApplication(next);
        }}>
          <option value="">全部应用</option>
          {applications.map((candidate) => <option key={candidate.appId} value={candidate.appId}>{candidate.name} · {candidate.appKey}</option>)}
        </select>
        <Icon name="chevron-down" size={15} />
      </label>

      <nav className="nav" aria-label="开放平台导航">
        <div className="nav-primary">
          {globalNavigation.map((item) => <NavigationButton active={route.view === item.view} item={item} key={item.view} onClick={() => { onNavigate(item.view); onCloseNavigation(); }} />)}
        </div>
        {application === null || !appScoped ? null : <>
          <div className="nav-context-label"><span>{application.name}</span><small>应用工作台</small></div>
          {applicationNavigation.map((section) => {
            const items = section.items.filter((item) => item.permission === undefined || permissions.includes(item.permission));
            if (items.length === 0) return null;
            const expanded = expandedSections.has(section.id);
            const containsCurrentView = items.some((item) => item.view === route.view);
            const submenuId = `nav-submenu-${section.id}`;
            return <div className="nav-section" key={section.id}>
              <button aria-controls={submenuId} aria-expanded={expanded} className={`nav-parent ${containsCurrentView ? "contains-active" : ""}`} type="button" onClick={() => { toggleSection(section.id); }}>
                <span>{section.label}</span><Icon className="nav-parent-chevron" name="chevron-down" size={15} />
              </button>
              <div className="nav-submenu" hidden={!expanded} id={submenuId}>
                {items.map((item) => <NavigationButton active={route.view === item.view} item={item} key={item.view} onClick={() => { onNavigate(item.view); onCloseNavigation(); }} />)}
              </div>
            </div>;
          })}
        </>}
      </nav>

      <div className={`service-status service-${health}`}>
        <div><span className="service-dot" /><strong>{health === "ok" ? "平台服务正常" : health === "checking" ? "正在检查服务" : "服务状态未知"}</strong></div>
        <p>{health === "ok" ? "控制面健康检查已通过" : "请查看状态页或稍后重试"}</p>
        <small>Console v{APP_VERSION}</small>
      </div>
    </aside>

    <main className="main">
      <header className="topbar">
        <div className="topbar-title"><IconButton className="mobile-menu-button" icon="menu" label="打开导航" onClick={onOpenNavigation} /><div><strong>{contextTitle}</strong><p>{contextSubtitle}</p></div></div>
        <div className="topbar-actions">
          {appScoped && application !== null ? <>
            <button className="documentation-link" type="button" onClick={() => { onNavigate("quickstart"); }}><Icon name="book" size={16} />接入文档</button>
            <span className="topbar-divider" />
          </> : null}
          <span className="avatar">{initials(developer?.name)}</span>
          <span className="user-summary"><strong>{developer?.name ?? "开发者"}</strong><small>{developer?.email ?? "Sonox Account"}</small></span>
          <Button leadingIcon="logout" size="small" variant="ghost" onClick={onLogout}>退出</Button>
        </div>
      </header>
      <div className="content">{children}</div>
    </main>
  </div>;
}

function NavigationButton({ item, active, onClick }: {
  readonly item: NavigationItem;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return <button aria-current={active ? "page" : undefined} className={`nav-item ${active ? "active" : ""}`} title={item.description} type="button" onClick={onClick}><Icon name={item.icon} /><span><strong>{item.label}</strong></span></button>;
}
